/**
 * Default wizard footer renderer: Back button on the left, then secondary
 * CTAs, then the primary CTA on the right.
 *
 * CTAs are resolved against the WizardCtx:
 *   - `label`, `isDisabled`, `isLoading` can be functions of ctx
 *   - The primary CTA defaults to Next on non-final steps and Confirm on
 *     the final step; consumers can override per step via `step.primaryCta`
 *
 * If `step.hideFooter` is true, this renders nothing.
 */

import { ChevronLeft, ChevronRight } from '@untitledui/icons';
import { useCallback, useMemo } from 'react';

import { Button } from '@/lib/base/buttons/button';
import { cx } from '@/utils/cx';

import type { WizardCta, WizardCtx, WizardStep } from './types';

interface WizardFooterProps {
  step: WizardStep;
  ctx: WizardCtx;
  /**
   * Max width (px) of the footer's inner row, so the CTAs align with the step's
   * content column. Defaults to max-w-2xl (672px) when unset; steps with a custom
   * `maxContentWidth` pass it here so the primary CTA lines up with the wider content
   * (otherwise the right-aligned Next button sits inside the content's right edge).
   */
  maxWidth?: number;
  /**
   * Show a Cancel button (leftmost) that closes the wizard via `ctx.close`.
   * Closing routes through the dirty-close confirmation when the wizard is
   * dirty. Default: false.
   */
  showCancel?: boolean;
  /**
   * Triggered by the default Next CTA on non-final steps (after
   * `step.onBeforeNext` resolves).
   */
  onNextRequest: () => Promise<void> | void;
  /**
   * Triggered by the default Confirm CTA on the final step. Wizard owns
   * the `isSubmitting` flag while this resolves.
   */
  onConfirmRequest: () => Promise<void> | void;
  /**
   * Invokes a CTA's custom `onClick` under the wizard's submission
   * tracking — sets `isSubmitting=true` for the duration of the returned
   * promise and swallows rejections (the CTA's step body owns surfacing
   * errors).
   */
  onCtaClickRequest: (
    onClick: (ctx: WizardCtx) => Promise<void> | void
  ) => Promise<void>;
}

function resolveValue<T>(
  value: T | ((ctx: WizardCtx) => T),
  ctx: WizardCtx
): T {
  return typeof value === 'function'
    ? (value as (ctx: WizardCtx) => T)(ctx)
    : value;
}

interface CtaButtonProps {
  cta: WizardCta;
  ctx: WizardCtx;
  defaultColor: 'primary' | 'secondary' | 'tertiary';
  defaultLoading?: boolean;
  defaultDisabled?: boolean;
  onClickFallback?: () => Promise<void> | void;
  onCtaClickRequest: (
    onClick: (ctx: WizardCtx) => Promise<void> | void
  ) => Promise<void>;
  iconTrailing?: typeof ChevronRight;
}

function CtaButton({
  cta,
  ctx,
  defaultColor,
  defaultLoading = false,
  defaultDisabled = false,
  onClickFallback,
  onCtaClickRequest,
  iconTrailing,
}: CtaButtonProps) {
  const label = resolveValue(cta.label, ctx);
  const isLoading =
    (cta.isLoading !== undefined ? resolveValue(cta.isLoading, ctx) : false) ||
    defaultLoading;
  const isDisabled =
    (cta.isDisabled !== undefined
      ? resolveValue(cta.isDisabled, ctx)
      : false) || defaultDisabled;
  const color = cta.variant ?? defaultColor;

  const handleClick = useCallback(() => {
    if (cta.onClick) {
      // Route through the wizard so async CTAs get `isSubmitting`
      // tracking and promise rejections are handled the same way as
      // `onConfirm` / `onBeforeNext`.
      void onCtaClickRequest(cta.onClick);
      return;
    }
    if (onClickFallback) {
      void onClickFallback();
    }
  }, [cta, onClickFallback, onCtaClickRequest]);

  return (
    <Button
      size='sm'
      color={color}
      iconTrailing={iconTrailing}
      onClick={handleClick}
      isDisabled={isDisabled || isLoading}
      isLoading={isLoading}
      // Steps author their loading copy as a function of ctx (e.g. "Verifying",
      // "Starting import"). Button hides all children while loading unless told
      // otherwise, so without this the label is computed and then thrown away —
      // every wizard collapsed to a bare spinner with no indication of what it
      // was doing, for however long the step took.
      showTextWhileLoading
    >
      {label}
    </Button>
  );
}

export function WizardFooter({
  step,
  ctx,
  maxWidth,
  showCancel = false,
  onNextRequest,
  onConfirmRequest,
  onCtaClickRequest,
}: WizardFooterProps) {
  // Hook must be called before any early return.
  // Resolve the primary CTA. Default: Next (non-final) or Confirm (final).
  const primary = useMemo<WizardCta>(
    () =>
      step.primaryCta && step.primaryCta !== 'auto'
        ? step.primaryCta
        : { label: ctx.isLast ? 'Confirm' : 'Next' },
    [step.primaryCta, ctx.isLast]
  );

  if (step.hideFooter) {
    return null;
  }

  const showBack = !ctx.isFirst && !step.hideBackButton;

  const primaryFallback = ctx.isLast ? onConfirmRequest : onNextRequest;
  const primaryDefaultDisabled = ctx.isSubmitting;
  const primaryDefaultLoading = ctx.isSubmitting;

  return (
    <div
      className={cx(
        'mx-auto flex w-full items-center justify-between gap-3',
        // Default alignment width; a step's maxContentWidth overrides via style.
        !maxWidth && 'max-w-2xl'
      )}
      style={maxWidth ? { maxWidth: `${maxWidth}px` } : undefined}
    >
      <div className='flex items-center gap-3'>
        {showCancel && (
          <Button
            size='sm'
            color='tertiary'
            onClick={ctx.close}
            isDisabled={ctx.isSubmitting}
          >
            Cancel
          </Button>
        )}
        {showBack && (
          <Button
            size='sm'
            color='secondary'
            iconLeading={ChevronLeft}
            onClick={ctx.back}
            isDisabled={ctx.isSubmitting}
          >
            Back
          </Button>
        )}
      </div>

      <div className='flex items-center gap-3'>
        {step.secondaryCtas?.map(cta => (
          <CtaButton
            key={`secondary-${cta.id}`}
            cta={cta}
            ctx={ctx}
            defaultColor='secondary'
            defaultDisabled={ctx.isSubmitting}
            onCtaClickRequest={onCtaClickRequest}
          />
        ))}
        <CtaButton
          cta={primary}
          ctx={ctx}
          defaultColor='primary'
          defaultLoading={primaryDefaultLoading}
          defaultDisabled={primaryDefaultDisabled}
          onClickFallback={primaryFallback}
          onCtaClickRequest={onCtaClickRequest}
          iconTrailing={ctx.isLast ? undefined : ChevronRight}
        />
      </div>
    </div>
  );
}
